import React, { ReactElement, ReactNode } from "react";
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldError,
  FieldPath,
  FieldValues,
} from "react-hook-form";

import { Stack, Typography } from "@components/base";

type RenderArgs<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  error?: FieldError;
  disabled?: boolean;
};

export interface LabeledControllerFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  label: ReactNode;
  name: TName;
  control: Control<TFieldValues, Record<string, unknown>, unknown>;
  spacing?: number;
  disabled?: boolean;
  render: (args: RenderArgs<TFieldValues, TName>) => ReactElement;
}

const LabeledControllerField = function <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  label,
  name,
  control,
  spacing = 1,
  disabled,
  render,
}: LabeledControllerFieldProps<TFieldValues, TName>): ReactElement {
  return (
    <Stack spacing={spacing}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState: { error } }) =>
          render({ field, error, disabled })
        }
      />
    </Stack>
  );
};

export default LabeledControllerField;
